import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft, FiSend, FiCopy, FiAlertCircle, FiUserPlus, FiUser, FiFileText, FiBookOpen, FiCheckSquare,
} from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui/Spinner';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { useAuth } from '../../contexts/AuthContext';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  pssPortalService, ESCOLARIDADE_LABEL,
  type EditalPublico, type Escolaridade,
} from '../../services/pss.service';

export function PssInscreverPage() {
  const { numero } = useParams<{ numero: string }>();
  const navigate = useNavigate();
  const { cidadao, cadastroCompleto } = useAuth();

  const [edital, setEdital] = useState<EditalPublico | null>(null);
  const [carregandoEdital, setCarregandoEdital] = useState(true);

  const [candidatoRg, setCandidatoRg] = useState('');
  const [pcd, setPcd] = useState(false);
  const [escolaridade, setEscolaridade] = useState<Escolaridade>('medio');
  const [formacao, setFormacao] = useState('');
  const [experienciaAnos, setExperienciaAnos] = useState<number | ''>('');
  const [experienciaResumo, setExperienciaResumo] = useState('');
  const [isencaoSolicitada, setIsencaoSolicitada] = useState(false);
  const [aceiteVeracidade, setAceiteVeracidade] = useState(false);

  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<{ numero: string } | null>(null);

  useEffect(() => {
    if (!numero) return;
    pssPortalService.buscarEdital(getEstabelecimentoAtualId(), numero)
      .then(setEdital)
      .finally(() => setCarregandoEdital(false));
  }, [numero]);

  useEffect(() => {
    if (edital?.escolaridade_minima) setEscolaridade(edital.escolaridade_minima);
  }, [edital]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!cidadao || !edital) return;
    if (!candidatoRg.trim()) { setErro('Informe o número do RG.'); return; }
    if (formacao.trim().length < 5) { setErro('Descreva sua formação.'); return; }
    if (!aceiteVeracidade) { setErro('Você precisa aceitar a declaração de veracidade.'); return; }

    setErro(null);
    setEnviando(true);
    try {
      const r = await pssPortalService.inscrever({
        estabelecimento_id: getEstabelecimentoAtualId(),
        cidadao_id: cidadao.id,
        edital_id: edital.id,
        titulo: `Inscrição PSS — ${cidadao.nome}`,
        candidato_nome: cidadao.nome,
        candidato_cpf: cidadao.cpf ?? '',
        candidato_rg: candidatoRg.trim(),
        candidato_email: cidadao.email,
        candidato_telefone: cidadao.telefone ?? undefined,
        pcd,
        escolaridade,
        formacao: formacao.trim(),
        experiencia_anos: experienciaAnos === '' ? undefined : Number(experienciaAnos),
        experiencia_resumo: experienciaResumo.trim() || undefined,
        isencao_solicitada: isencaoSolicitada,
      });
      setResultado({ numero: r.numero });
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao inscrever.');
    } finally {
      setEnviando(false);
    }
  }

  if (carregandoEdital) {
    return (
      <PortalPage titulo="Inscrição" icone={<FiUserPlus size={26} />}>
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      </PortalPage>
    );
  }

  if (!edital) {
    return (
      <PainelResultado
        titulo="Edital não encontrado"
        tom="aviso"
        icone={<FiAlertCircle size={32} />}
        acoes={
          <Button variant="outline" onClick={() => navigate('/pss')}>
            <FiArrowLeft size={14} /> Voltar
          </Button>
        }
      />
    );
  }

  if (!cadastroCompleto) {
    return (
      <PortalPage
        titulo="Inscrição"
        subtitulo={`${edital.numero} · ${edital.titulo}`}
        icone={<FiUserPlus size={26} />}
        voltarHref={`/pss/${edital.numero}`}
        voltarLabel="Voltar ao edital"
      >
        <SectionCard titulo="Complete seu cadastro" icone={<FiUser size={18} />}>
          <Alert variante="aviso">
            Para se inscrever em um processo seletivo você precisa completar seu cadastro
            (CPF, telefone e endereço).
          </Alert>
          <div className="flex justify-end mt-4">
            <Button onClick={() => navigate('/completar-cadastro')}>Completar cadastro</Button>
          </div>
        </SectionCard>
      </PortalPage>
    );
  }

  if (resultado) {
    return <PainelSucesso numeroInscricao={resultado.numero} numeroEdital={edital.numero} onVer={() => navigate('/minhas-inscricoes')} />;
  }

  if (edital.status_pss !== 'aberto') {
    return (
      <PainelResultado
        titulo="Inscrições encerradas"
        descricao="As inscrições para este edital não estão abertas."
        tom="aviso"
        icone={<FiAlertCircle size={32} />}
        acoes={
          <Button variant="outline" onClick={() => navigate(`/pss/${edital.numero}`)}>
            <FiArrowLeft size={14} /> Ver edital
          </Button>
        }
      />
    );
  }

  return (
    <PortalPage
      titulo="Inscrição"
      subtitulo={`${edital.numero} · ${edital.titulo}${edital.cargo ? ` · Cargo: ${edital.cargo}` : ''}`}
      icone={<FiUserPlus size={26} />}
      voltarHref={`/pss/${edital.numero}`}
      voltarLabel="Voltar ao edital"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {erro && <Alert variante="erro">{erro}</Alert>}

        <SectionCard titulo="Seus dados (do cadastro)" icone={<FiUser size={18} />}>
          <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm space-y-1">
            <div><strong>Nome:</strong> {cidadao?.nome}</div>
            {cidadao?.cpf && <div><strong>CPF:</strong> {cidadao.cpf}</div>}
            <div><strong>E-mail:</strong> {cidadao?.email}</div>
            {cidadao?.telefone && <div><strong>Telefone:</strong> {cidadao.telefone}</div>}
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Para alterar esses dados, edite seu perfil antes de se inscrever.
          </p>
        </SectionCard>

        <SectionCard titulo="Documentos pessoais" icone={<FiFileText size={18} />}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="rg" obrigatorio>Número do RG</Label>
              <Input id="rg" value={candidatoRg} onChange={(e) => setCandidatoRg(e.target.value)} required placeholder="ex: 12.345.678-9" />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={pcd} onChange={(e) => setPcd(e.target.checked)} className="mt-0.5" />
              <span>
                Sou pessoa com deficiência (PCD) e quero concorrer às vagas reservadas.
                <p className="text-xs text-slate-500 mt-0.5">
                  Você precisará anexar laudo médico (CID) na próxima etapa.
                </p>
              </span>
            </label>
          </div>
        </SectionCard>

        <SectionCard titulo="Formação e experiência" icone={<FiBookOpen size={18} />}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="esc" obrigatorio>Escolaridade efetiva</Label>
              <select
                id="esc"
                value={escolaridade}
                onChange={(e) => setEscolaridade(e.target.value as Escolaridade)}
                required
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
              >
                {(Object.keys(ESCOLARIDADE_LABEL) as Escolaridade[]).map((e) => (
                  <option key={e} value={e}>{ESCOLARIDADE_LABEL[e]}</option>
                ))}
              </select>
              {edital.escolaridade_minima && (
                <p className="text-xs text-slate-500 mt-1">
                  Escolaridade mínima exigida pelo edital: <strong>{ESCOLARIDADE_LABEL[edital.escolaridade_minima]}</strong>
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="formacao" obrigatorio>Formação acadêmica</Label>
              <textarea
                id="formacao"
                value={formacao}
                onChange={(e) => setFormacao(e.target.value)}
                rows={3}
                required
                placeholder="Ex: Pedagogia - UFRJ - Concluído em 2020. Especialização em Educação Inclusiva."
                className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm resize-y"
              />
            </div>
            <div>
              <Label htmlFor="exp-anos">Anos de experiência na área</Label>
              <Input id="exp-anos" type="number" min="0" value={experienciaAnos} onChange={(e) => setExperienciaAnos(e.target.value === '' ? '' : Number(e.target.value))} placeholder="0" />
            </div>
            <div>
              <Label htmlFor="exp-res">Resumo da experiência profissional</Label>
              <textarea
                id="exp-res"
                value={experienciaResumo}
                onChange={(e) => setExperienciaResumo(e.target.value)}
                rows={4}
                placeholder="Ex: 2021-2024 — Professor da rede municipal de XYZ; 2019-2021 — Estagiário em escola particular ABC..."
                className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm resize-y"
              />
            </div>
          </div>
        </SectionCard>

        {edital.permite_isencao && edital.valor_taxa != null && edital.valor_taxa > 0 && (
          <SectionCard titulo="Isenção da taxa" icone={<FiFileText size={18} />}>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={isencaoSolicitada} onChange={(e) => setIsencaoSolicitada(e.target.checked)} className="mt-0.5" />
              <span>
                Solicito isenção da taxa de inscrição (Lei 13.656/2018)
                <p className="text-xs text-slate-500 mt-0.5">
                  Você precisará comprovar hipossuficiência (CadÚnico, doação de medula óssea ou doadores regulares de sangue)
                  anexando documentos na próxima etapa.
                </p>
              </span>
            </label>
          </SectionCard>
        )}

        <SectionCard titulo="Declaração de veracidade" icone={<FiCheckSquare size={18} />}>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={aceiteVeracidade} onChange={(e) => setAceiteVeracidade(e.target.checked)} className="mt-0.5" required />
            <span>
              <strong>Declaro</strong> que as informações prestadas são verdadeiras, sob pena das sanções
              previstas em lei (art. 299 do Código Penal — falsidade ideológica). Estou ciente de que a
              inscrição não garante a contratação e que serei avaliado conforme os critérios do edital.
            </span>
          </label>
        </SectionCard>

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(`/pss/${edital.numero}`)}>Cancelar</Button>
          <Button type="submit" loading={enviando}>
            <FiSend size={14} /> Enviar inscrição
          </Button>
        </div>
      </form>
    </PortalPage>
  );
}

function PainelSucesso({ numeroInscricao, numeroEdital, onVer }: { numeroInscricao: string; numeroEdital: string; onVer: () => void }) {
  const [copiado, setCopiado] = useState(false);
  function copiar() {
    navigator.clipboard.writeText(numeroInscricao);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <PainelResultado
      titulo="Inscrição realizada"
      descricao={<>Edital <strong className="text-slate-900">{numeroEdital}</strong> · Sua inscrição é <strong className="text-slate-900">{numeroInscricao}</strong></>}
      acoes={<Button onClick={onVer}>Ver minhas inscrições</Button>}
    >
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <Label>Número da inscrição</Label>
        <div className="flex items-center gap-2 mt-1">
          <Input value={numeroInscricao} readOnly className="text-sm font-mono flex-1" />
          <Button type="button" variant="outline" onClick={copiar}>
            <FiCopy size={14} /> {copiado ? 'Copiado!' : 'Copiar'}
          </Button>
        </div>
      </div>

      <Alert variante="info" className="mt-4">
        Você receberá notificações por e-mail conforme o cronograma do edital.
        Não esqueça de anexar os documentos exigidos no prazo.
      </Alert>
    </PainelResultado>
  );
}
